'use client';

import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';

import { useBodyScrollLock, useT, useTransferFunds } from '@/hooks';
import { usePortfolioSummary } from '@/services/query';
import { formatCurrency } from '@/utils';
import { DepositHeader } from './subcomponents/deposit/DepositHeader';

import { PUSD_CONTRACT_ADDRESS, USDC_E_CONTRACT_ADDRESS } from '@/constants/tokens.constants';

interface TransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
}

export function TransferModal({ isOpen, onClose, onBack }: TransferModalProps) {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const { transfer, isPending } = useTransferFunds();
  const { data: summary } = usePortfolioSummary();
  const { t } = useT();

  useBodyScrollLock(isOpen);

  const [recipient, setRecipient] = useState('');
  const [amount, setAmount] = useState('');
  const [asset, setAsset] = useState<`0x${string}`>(PUSD_CONTRACT_ADDRESS);

  const handleClose = useCallback(() => {
    setRecipient('');
    setAmount('');
    setAsset(PUSD_CONTRACT_ADDRESS);
    onClose();
  }, [onClose]);

  const handleTransfer = async () => {
    try {
      await transfer(asset, recipient as `0x${string}`, amount);
      handleClose();
    } catch (e) {
      // Error is handled by hook
    }
  };

  const getAvailableBalance = () => {
    if (asset === PUSD_CONTRACT_ADDRESS) return summary?.cashBalance || 0;
    if (asset === USDC_E_CONTRACT_ADDRESS) return summary?.legacyUsdcBalance || 0;
    return 0;
  };

  const availableBalance = getAvailableBalance();
  const isValidAddress = /^0x[a-fA-F0-9]{40}$/.test(recipient);
  const isValidAmount = Number(amount) > 0 && Number(amount) <= availableBalance;

  return (
    <Dialog
      open={isOpen}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && !isPending) {
          handleClose();
        }
      }}
      disableEscapeKeyDown={isPending}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        className: `font-inherit transition-all duration-300 ${
          isMobile
            ? 'h-full w-full max-w-none rounded-none p-0'
            : 'max-h-[85vh] w-full max-w-[480px] rounded-[32px] p-6'
        } flex flex-col overflow-y-auto scrollbar-none bg-[var(--bg-primary)] text-[var(--text-primary)] shadow-2xl dark:bg-[#0a0e1a] m-0`,
        style: {
          backgroundImage: 'none',
          maxWidth: '480px',
        },
      }}
      BackdropProps={{
        className: 'bg-black/60 backdrop-blur-sm',
      }}
    >
      <DialogContent
        sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <Box className={`z-10 flex h-full flex-col ${isMobile ? 'pt-4' : ''}`}>
          <Box className={isMobile ? 'px-4' : ''}>
            <DepositHeader
              balance={summary?.cashBalance || 0}
              title={t('transferModal.title')}
              onClose={handleClose}
              onBack={onBack}
            />
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                p: 3,
                pt: 0,
              }}
            >
              {isPending ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <CircularProgress size={48} sx={{ color: '#0085ff', mb: 3 }} />
                  <Typography variant="h6" sx={{ color: 'var(--text-primary)' }}>
                    {t('transferModal.processingTransfer')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'var(--text-muted)', mt: 1 }}>
                    {t('transferModal.confirmInWallet')}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Asset Selection */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'var(--text-muted)', mb: 1, display: 'block' }}
                    >
                      {t('transferModal.asset')}
                    </Typography>
                    <Select
                      value={asset}
                      onChange={(e) => setAsset(e.target.value as `0x${string}`)}
                      fullWidth
                      size="small"
                      sx={{
                        borderRadius: '12px',
                        color: 'var(--text-primary)',
                        bgcolor: 'var(--bg-secondary)',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--border-color)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--text-muted)',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'var(--text-primary)',
                        },
                        '& .MuiSvgIcon-root': { color: 'var(--text-muted)' },
                      }}
                    >
                      <MenuItem value={PUSD_CONTRACT_ADDRESS}>{t('transferModal.pusdBalance')}</MenuItem>
                      <MenuItem value={USDC_E_CONTRACT_ADDRESS}>{t('transferModal.usdcLegacy')}</MenuItem>
                    </Select>
                  </Box>

                  {/* Recipient Address */}
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: 'var(--text-muted)', mb: 1, display: 'block' }}
                    >
                      {t('transferModal.recipientAddress')}
                    </Typography>
                    <TextField
                      fullWidth
                      placeholder="0x..."
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      size="small"
                      error={recipient.length > 0 && !isValidAddress}
                      helperText={
                        recipient.length > 0 && !isValidAddress ? t('transferModal.invalidAddress') : ''
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          color: 'var(--text-primary)',
                          bgcolor: 'var(--bg-secondary)',
                          '& fieldset': { borderColor: 'var(--border-color)' },
                          '&:hover fieldset': { borderColor: 'var(--text-muted)' },
                          '&.Mui-focused fieldset': { borderColor: 'var(--text-primary)' },
                        },
                        '& .MuiFormHelperText-root': { color: '#ef4444' },
                      }}
                    />
                  </Box>

                  {/* Amount */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                        {t('transferModal.amount')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'var(--text-muted)' }}>
                        {t('transferModal.available')} {formatCurrency(availableBalance)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        fullWidth
                        placeholder="0.00"
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            color: 'var(--text-primary)',
                            bgcolor: 'var(--bg-secondary)',
                            '& fieldset': { borderColor: 'var(--border-color)' },
                            '&:hover fieldset': { borderColor: 'var(--text-muted)' },
                            '&.Mui-focused fieldset': { borderColor: 'var(--text-primary)' },
                          },
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={() => setAmount(availableBalance.toString())}
                        sx={{
                          borderRadius: '12px',
                          borderColor: 'var(--border-color)',
                          color: 'var(--text-primary)',
                          minWidth: 'auto',
                          px: 2,
                        }}
                      >
                        {t('transferModal.max')}
                      </Button>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleTransfer}
                    disabled={!isValidAddress || !isValidAmount || isPending}
                    sx={{
                      mt: 2,
                      py: 1.5,
                      borderRadius: '16px',
                      bgcolor: '#0085ff',
                      color: 'white',
                      fontWeight: 700,
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#0074e0' },
                      '&.Mui-disabled': {
                        bgcolor: 'var(--bg-secondary)',
                        color: 'var(--text-muted)',
                        border: '1px solid var(--border-color)',
                      },
                    }}
                  >
                    {t('transferModal.transferButton')}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
